import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { Accordion } from '../../../../components/accordion/accordion';
import { AccordionDetails } from '../../../../components/accordion/accordion-details';
import { AccordionSummary } from '../../../../components/accordion/accordion-summary';
import { AreaContainer } from '../../../../components/area-layout/area-container';
import { AreaContent } from '../../../../components/area-layout';
import { CitizenLookup } from '../../../../components/lookups/citizen-lookup/citizen-lookup';
import { ICitizenLookup } from '../../../../components/lookups/citizen-lookup/interfaces';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { INudgeCitizen, NudgeCitizenList } from './nudge-citizen-list';
import { NudgeWithNews } from './nudge-with-news';
import { NudgeWithQuestionnaire } from './nudge-with-questionnaire';
import { useForumCaseworkerContext } from '../use-forum-caseworker-context';
import { useNudgeNotifier } from './use-nudge-notifier';

const client = moduleClientFactory.createForumCaseworkerClient();

export const forumNudgeCitizenIds = 'forumNudgeCitizenIds';
export const maxNudgeCitizens = 250;

type NudgeKind = 'news' | 'questionnaire';

export interface IForumNudgeLocationState {
  nudgeCitizen?: ICitizenLookup;
}

// Worklist ids aren't guaranteed to match the BFF's lower-case GUID format.
function normalizeCitizenId(citizenId: string) {
  return citizenId.replace(/[{}]/g, '').toLowerCase();
}

export function ForumNudgeTab() {
  const [citizens, setCitizens] = useState<INudgeCitizen[]>([]);
  const requestedCitizenIds = useRef(new Set<string>());
  const { nudgeCitizen } = useLocation<IForumNudgeLocationState | undefined>().state ?? {};
  const { isFeedbackEnabled } = useForumCaseworkerContext();
  const [kind, setKind] = useState<NudgeKind>('news');
  const { notify } = useNudgeNotifier();
  usePostToTab<string[]>(forumNudgeCitizenIds, addCitizensFromWorklist);

  useEffect(() => {
    if (nudgeCitizen) {
      addCitizen(nudgeCitizen);
    }
  }, []);

  function addCitizen(picked: ICitizenLookup) {
    if (!picked?.citizenId || requestedCitizenIds.current.has(picked.citizenId) || !hasRoomFor(1)) {
      return;
    }
    requestedCitizenIds.current.add(picked.citizenId);
    setCitizens(current => [...current, { citizenId: picked.citizenId, name: picked.fullName ?? '', cpr: picked.cpr }]);
    loadAppStatus([picked.citizenId]);
  }

  async function addCitizensFromWorklist(citizenIds: string[]) {
    const newCitizenIds = Array.from(new Set(citizenIds.map(normalizeCitizenId))).filter(citizenId => !requestedCitizenIds.current.has(citizenId));
    if (newCitizenIds.length === 0 || !hasRoomFor(newCitizenIds.length)) {
      return;
    }
    newCitizenIds.forEach(citizenId => requestedCitizenIds.current.add(citizenId));

    try {
      const found = (await client.getNudgeCitizens({ citizenIds: newCitizenIds })).citizens ?? [];
      const foundCitizenIds = found.map(citizen => citizen.citizenId);
      newCitizenIds.filter(citizenId => !foundCitizenIds.includes(citizenId)).forEach(citizenId => requestedCitizenIds.current.delete(citizenId));
      setCitizens(current => [...current, ...found.map(citizen => ({ citizenId: citizen.citizenId, name: citizen.name ?? '', cpr: citizen.cpr }))]);

      const notLoadedCount = newCitizenIds.length - found.length;
      if (notLoadedCount > 0) {
        notify(`${notLoadedCount} ${notLoadedCount === 1 ? 'borger' : 'borgere'} kunne ikke indlæses og er ikke tilføjet.`, 'warning');
      }
      if (foundCitizenIds.length > 0) {
        loadAppStatus(foundCitizenIds);
      }
    } catch {
      newCitizenIds.forEach(citizenId => requestedCitizenIds.current.delete(citizenId));
      notify('Borgerne fra arbejdslisten kunne ikke indlæses.', 'error');
    }
  }

  async function loadAppStatus(citizenIds: string[]) {
    try {
      const result = await client.getCitizensAppStatus({ citizenIds });
      const hasAppByCitizenId = new Map((result.citizens ?? []).map(status => [status.citizenId, status.hasApp]));
      setCitizens(current =>
        current.map(citizen => (citizenIds.includes(citizen.citizenId) ? { ...citizen, hasApp: hasAppByCitizenId.get(citizen.citizenId) ?? false } : citizen))
      );
    } catch {
      setCitizens(current => current.map(citizen => (citizenIds.includes(citizen.citizenId) ? { ...citizen, appStatusFailed: true } : citizen)));
    }
  }

  function hasRoomFor(count: number) {
    if (requestedCitizenIds.current.size + count <= maxNudgeCitizens) {
      return true;
    }
    notify(`Der kan højst nudges ${maxNudgeCitizens} borgere ad gangen.`, 'warning');
    return false;
  }

  function removeCitizen(citizenId: string) {
    requestedCitizenIds.current.delete(citizenId);
    setCitizens(current => current.filter(citizen => citizen.citizenId !== citizenId));
  }

  return (
    <AreaContainer>
      <AreaContent>
        <Accordion defaultExpanded>
          <AccordionSummary>Nudge</AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              {isFeedbackEnabled && (
                <ToggleButtonGroup exclusive size="small" value={kind} onChange={(_event, value) => value && setKind(value)} aria-label="Nudge med">
                  <ToggleButton value="news">Nyhed</ToggleButton>
                  <ToggleButton value="questionnaire">Spørgeskema</ToggleButton>
                </ToggleButtonGroup>
              )}
              <Box display={kind === 'news' ? 'block' : 'none'}>
                <NudgeWithNews citizens={citizens} />
              </Box>
              {isFeedbackEnabled && (
                <Box display={kind === 'questionnaire' ? 'block' : 'none'}>
                  <NudgeWithQuestionnaire citizens={citizens} />
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded>
          <AccordionSummary>Borgere</AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <CitizenLookup label="Tilføj borger" onChange={addCitizen} />
              <NudgeCitizenList citizens={citizens} onRemove={removeCitizen} />
            </Box>
          </AccordionDetails>
        </Accordion>
      </AreaContent>
    </AreaContainer>
  );
}
